import { type ReactNode } from 'react'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { ApiError } from '../api/client'
import { reportError, reportForbidden } from '../lib/monitoring'

/**
 * 401/403 — штатный auth-флоу (истёкший токен / нет прав), а не сбой: тот же
 * осознанный фильтр, что в reportApiCall (lib/monitoring.shared.ts) — оба
 * молчат в reportError. 403 не совсем немой: см. reportForbidden ниже, где
 * onError зовёт его перед return — сломанная роль на бэке иначе невидима на
 * дашборде вообще. 401 остаётся полной тишиной.
 */
function isAuthFlow(error: unknown): error is ApiError {
  return error instanceof ApiError && (error.status === 401 || error.status === 403)
}

/**
 * Клиент один на приложение (его же зовёт AuthProvider: queryClient.clear() на
 * выходе из сессии).
 *
 * onError у кэшей — единственный способ увидеть отказы query/mutation в
 * мониторинге: ErrorBoundary ловит только краши рендера, а ошибки запросов
 * React Query перехватывает сам (экран показывает ошибку) и до boundary они не
 * долетают. Зовётся один раз — после финального провала, когда ретраи (retry: 1)
 * исчерпаны, так что одному упавшему запросу соответствует одно событие
 * reportError/reportForbidden (type:'api' из fetchTracked — событие другого
 * типа, issue из него не строится).
 *
 * source разводит отказы по разным issue (см. ErrorCtx в monitoring.shared.ts);
 * без него в дашборде будет один issue «ApiError: 500 Internal Server Error» на
 * всё приложение.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // только голова ключа: в хвосте id и пользовательский ввод
      // (['cmd-search', term]) — ему не место ни в заголовке issue, ни в его кардинальности
      const source = `query ${String(query.queryKey[0])}`
      if (isAuthFlow(error)) {
        if (error.status === 403) reportForbidden(source)
        return
      }
      reportError(error, { source })
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _onMutate, mutation) => {
      // mutationKey сейчас не задаёт ни один useMutation — без него мутации
      // группируются по сообщению бэка; задать ключ в хуке = отдельный issue
      const key = mutation.options.mutationKey?.[0]
      const source = key ? `mutation ${String(key)}` : 'mutation'
      if (isAuthFlow(error)) {
        if (error.status === 403) reportForbidden(source)
        return
      }
      reportError(error, { source })
    },
  }),
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
})

export function QueryProvider({ children }: { children: ReactNode }) {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
}
